import { AuthUser } from "../types/auth";

/**
 * Determines the appropriate dashboard route based on user roles
 * @param user - The authenticated user object
 * @returns The dashboard route path
 */
export const getDashboardRoute = (user: AuthUser | null): string => {
  if (!user || !user.roles || user.roles.length === 0) {
    return "/dashboard"; // Default fallback
  }

  // Check roles in order of priority (highest to lowest)
  if (user.roles.includes("superadmin")) {
    return "/superadmin";
  }

  if (user.roles.includes("admin")) {
    return "/admin/dashboard";
  }

  if (user.roles.includes("secretary")) {
    return "/secretary";
  }

  if (user.roles.includes("teacher")) {
    return "/teacher/dashboard"; // Teachers go to teacher dashboard
  }

  if (user.roles.includes("student")) {
    return "/student/dashboard"; // Students go to student dashboard
  }

  // Default fallback
  return "/dashboard";
};

/**
 * Checks if user has any of the specified roles
 * @param user - The authenticated user object
 * @param roles - Array of roles to check
 * @returns True if user has any of the specified roles
 */
export const hasAnyRole = (user: AuthUser | null, roles: string[]): boolean => {
  if (!user || !user.roles) return false;
  return roles.some((role) => user.roles.includes(role));
};

/**
 * Checks if user has a specific role
 * @param user - The authenticated user object
 * @param role - The role to check
 * @returns True if user has the specified role
 */
export const hasRole = (user: AuthUser | null, role: string): boolean => {
  if (!user || !user.roles) return false;
  return user.roles.includes(role);
};

/**
 * Gets the highest priority role for display purposes
 * @param user - The authenticated user object
 * @returns The highest priority role or null
 */
export const getPrimaryRole = (user: AuthUser | null): string | null => {
  if (!user || !user.roles || user.roles.length === 0) {
    return null;
  }

  const rolePriority = {
    superadmin: 1,
    admin: 2,
    secretary: 3,
    teacher: 4,
    student: 5,
  };

  // Sort roles by priority and return the highest priority role
  const sortedRoles = user.roles.sort((a, b) => {
    const aPriority = rolePriority[a as keyof typeof rolePriority] || 999;
    const bPriority = rolePriority[b as keyof typeof rolePriority] || 999;
    return aPriority - bPriority;
  });

  return sortedRoles[0] || null;
};

/**
 * Gets user display name based on role
 * @param user - The authenticated user object
 * @returns Formatted display name
 */
export const getUserDisplayName = (user: AuthUser | null): string => {
  if (!user) return "User";

  const primaryRole = getPrimaryRole(user);
  if (!primaryRole) return "User";

  const roleDisplayNames = {
    superadmin: "Super Administrator",
    admin: "Administrator",
    secretary: "Secretary",
    teacher: "Teacher",
    student: "Student",
  };

  return (
    roleDisplayNames[primaryRole as keyof typeof roleDisplayNames] || "User"
  );
};
